import { TabsContent } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useEffect, useState } from "react";
import axios from '../../../lib/axios';
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { useIsUserAdmin } from "@/hooks/authHelpers";
import { Separator } from "@/components/ui/separator";

export default function NotificationsTab() {
    const { id: communityId } = useParams();
    const { toast } = useToast();
    const [settings, setSettings] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    const [newWeeklyDigestEmail, setNewWeeklyDigestEmail] = useState("");
    const [newPostCallEmail, setNewPostCallEmail] = useState("");

    const isAdmin = useIsUserAdmin();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get(`/communities/${communityId}/settings`);
                setSettings(response.data);
            } catch (error) {
                console.error('Error fetching data:', error);
                toast({
                    title: "Error",
                    description: "Failed to load community details. Please try again.",
                    variant: "destructive",
                });
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [communityId, toast]);


    const handleChange = async (data) => {
        setIsLoading(true);

        try {
            const response = await axios.patch(`/communities/${communityId}/settings`, {
                ...settings,
                ...data,
            });

            setSettings({ ...response.data });

            toast({
                title: "Success",
                description: "Updated community settings!",
            });
        } catch (error) {
            console.error('Failed to update settings:', error);
            toast({
                title: "Error",
                description: "Failed to update settings.",
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleHasWeeklyEmailDigest = async (checked) => {
        if (!isAdmin) {
            return;
        }

        handleChange({ has_weekly_email_digest: checked });
    };

    const handleHasPostCallEmail = async (checked) => {
        if (!isAdmin) {
            return;
        }

        handleChange({ has_post_call_email: checked });
    };


    const handleAddWeeklyDigestEmail = () => {
        if (!isAdmin) return;

        if (!newWeeklyDigestEmail || !/^\S+@\S+\.\S+$/.test(newWeeklyDigestEmail)) {
            toast({
                title: "Invalid Email",
                description: "Please enter a valid email address.",
                variant: "destructive",
            });
            return;
        }

        if (settings.weekly_email_digest_emails.includes(newWeeklyDigestEmail)) {
            toast({
                title: "Duplicate Email",
                description: "This email is already added.",
                variant: "destructive",
            });
            return;
        }

        const updatedEmails = [...settings.weekly_email_digest_emails, newWeeklyDigestEmail];

        handleChange({ weekly_email_digest_emails: updatedEmails });
        setNewWeeklyDigestEmail("");
    };


    const handleAddPostCallEmail = () => {
        if (!isAdmin) return;

        if (!newPostCallEmail || !/^\S+@\S+\.\S+$/.test(newPostCallEmail)) {
            toast({
                title: "Invalid Email",
                description: "Please enter a valid email address.",
                variant: "destructive",
            });
            return;
        }

        if (settings.post_call_emails.includes(newPostCallEmail)) {
            toast({
                title: "Duplicate Email",
                description: "This email is already added.",
                variant: "destructive",
            });
            return;
        }

        const updatedEmails = [...settings.post_call_emails, newPostCallEmail];

        handleChange({ post_call_emails: updatedEmails });
        setNewPostCallEmail("");
    };


    const handleRemoveWeeklyDigestEmail = (emailToRemove) => {
        if (!isAdmin) return;

        const updatedEmails = settings.weekly_email_digest_emails.filter(email => email !== emailToRemove);
        handleChange({ weekly_email_digest_emails: updatedEmails });
    };

    const handleRemovePostCallEmail = (emailToRemove) => {
        if (!isAdmin) return;

        const updatedEmails = settings.post_call_emails.filter(email => email !== emailToRemove);
        handleChange({ post_call_emails: updatedEmails });
    };

    if (isLoading && !settings?.community_id) {
        return (
            <TabsContent value="notifications">
                <div className="grid grid-cols-1 gap-16 mt-4 max-w-3xl">
                    {/* 3 switch rows + number input + phone input */}
                    {Array.from({ length: 2 }).map((_, i) => (
                        <div className="flex items-center justify-between space-x-2" key={i}>
                            <div className="space-y-2 w-full">
                                <Skeleton className="h-4 w-48" /> {/* fake label */}
                                <Skeleton className="h-3 w-full max-w-md" /> {/* fake hint */}
                            </div>
                            <Skeleton className="h-6 w-12 rounded-full" /> {/* fake switch */}
                        </div>
                    ))}

                    {/* number input skeleton */}
                    <div className="flex items-center justify-between space-x-2">
                        <div className="space-y-2 w-full">
                            <Skeleton className="h-4 w-72" />
                            <Skeleton className="h-3 w-full max-w-md" />
                        </div>
                        <Skeleton className="h-10 w-20 rounded-md" />
                    </div>

                    {/* phone number input skeleton */}
                    <div className="space-y-2">
                        <Skeleton className="h-4 w-72" />
                        <Skeleton className="h-3 w-full max-w-md" />
                        <Skeleton className="h-10 w-72 rounded-md" />
                    </div>
                </div>
            </TabsContent>
        );
    }

    return (

        <TabsContent value="notifications" className="grid grid-cols-1 gap-10 mt-4">
                <div className="grid grid-cols-2 gap-2">
                    <Label htmlFor="has-weekly-email-digest" className="flex flex-col space-y-1 justify-self-start">
                        <span>Weekly email digest</span>
                        <span className="text-sm text-muted-foreground">
                            When enabled, you'll receive a Flair digest email, CC'ing all recipients for group discussions.
                        </span>
                    </Label>
                    <Switch
                        className="justify-self-end"
                        id="has-weekly-email-digest"
                        checked={settings.has_weekly_email_digest}
                        onCheckedChange={handleHasWeeklyEmailDigest}
                        disabled={isLoading || !isAdmin}
                    />
                </div>
                {!!settings?.has_weekly_email_digest && <div>
                    <div className="grid grid-cols-2 gap-2">
                        <Label htmlFor="weekly-email-digest" className="flex flex-col space-y-1">
                            <span>Add weekly digest emails</span>
                            <span className="text-sm text-muted-foreground">
                                Enter emails one by one and click "Add". Each email will be CC'd in the digest.
                            </span>
                        </Label>

                        <div className="flex space-x-2">
                            <Input
                                id="weekly-email-digest"
                                type="email"
                                value={newWeeklyDigestEmail}
                                onChange={(e) => setNewWeeklyDigestEmail(e.target.value)}
                                placeholder="Enter email"
                                disabled={isLoading || !isAdmin}
                            />
                            <Button
                                onClick={handleAddWeeklyDigestEmail}
                                disabled={isLoading || !isAdmin}
                            >
                                Add
                            </Button>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                        {settings.weekly_email_digest_emails?.map((email, index) => (
                            <Badge key={index} variant="outline" className="flex items-center space-x-2">
                                <span>{email}</span>
                                <Button
                                    variant="ghost"
                                    size="xs"
                                    onClick={() => handleRemoveWeeklyDigestEmail(email)}
                                    disabled={isLoading || !isAdmin}
                                >
                                    ✕
                                </Button>
                            </Badge>
                        ))}
                    </div>
                </div>}

                <Separator />

                <div className="grid grid-cols-2 gap-2">
                    <Label htmlFor="has-post-call-email" className="flex flex-col space-y-1 justify-self-start">
                        <span>Post call email summary</span>
                        <span className="text-sm text-muted-foreground">
                            When enabled, you'll receive a Flair digest email to each recipient when a call is complete
                        </span>
                    </Label>
                    <Switch
                        className="justify-self-end"
                        id="has-post-call-email"
                        checked={settings.has_post_call_email}
                        onCheckedChange={handleHasPostCallEmail}
                        disabled={isLoading || !isAdmin}
                    />
                </div>
                {settings.has_post_call_email && <div>
                    <div className="grid grid-cols-2 gap-2">
                        <Label htmlFor="post-call-email" className="flex flex-col space-y-1">
                            <span>Add post call emails</span>
                            <span className="text-sm text-muted-foreground">
                                Enter emails one by one and click "Add". Each email will be a recipient of a completed call summary.
                            </span>
                        </Label>

                        <div className="flex space-x-2">
                            <Input
                                id="post-call-email"
                                type="email"
                                value={newPostCallEmail}
                                onChange={(e) => setNewPostCallEmail(e.target.value)}
                                placeholder="Enter email"
                                disabled={isLoading || !isAdmin}
                            />
                            <Button
                                onClick={handleAddPostCallEmail}
                                disabled={isLoading || !isAdmin}
                            >
                                Add
                            </Button>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                        {settings.post_call_emails?.map((email, index) => (
                            <Badge key={index} variant="outline" className="flex items-center space-x-2">
                                <span>{email}</span>
                                <Button
                                    variant="ghost"
                                    size="xs"
                                    onClick={() => handleRemovePostCallEmail(email)}
                                    disabled={isLoading || !isAdmin}
                                >
                                    ✕
                                </Button>
                            </Badge>
                        ))}
                    </div>
                </div>}
        </TabsContent>
    )
}